'use client';
import { Button } from '@heroui/react';
import { createContext, ReactNode, useCallback, useContext, useState } from 'react';

interface ConfirmProps {
    title: string;
    message?: string;
    onConfirmed: () => void;
    onCancel?: () => void;
    allowClickOut?: boolean;
}

interface ConfirmContextType {
    showConfirm: (data: ConfirmProps) => void;
}

const ConfirmContext = createContext<ConfirmContextType | null>(null);

function ConfirmProvider({ children }: { children: ReactNode }) {
    const [confirm, setConfirm] = useState<ConfirmProps | undefined>(undefined);
    const showConfirm = useCallback((data: ConfirmProps) => {
        setConfirm(data);
    }, []);

    const onConfirmed = useCallback(async () => {
        await confirm?.onConfirmed();
        setConfirm(undefined);
    }, [confirm]);

    const onCancelled = useCallback(() => {
        confirm?.onCancel?.();
        setConfirm(undefined);
    }, [confirm]);

    return (
        <ConfirmContext.Provider value={{ showConfirm }}>
            {confirm && (
                <div
                    className='absolute z-50 h-screen w-screen bg-black bg-opacity-50'
                    onClick={confirm.allowClickOut ? () => setConfirm(undefined) : undefined}>
                    <div
                        className='m-auto mt-20 grid max-w-sm  rounded-sm bg-white  p-4'
                        onClick={e => e.stopPropagation()}>
                        <div className='mb-4 text-2xl font-semibold'>{confirm.title}</div>
                        {confirm.message && <div>{confirm.message}</div>}
                        <div className='grid grid-cols-2 gap-4'>
                            <Button variant='light' onPress={onCancelled}>
                                Cancel
                            </Button>
                            <Button variant='light' color='primary' onPress={onConfirmed}>
                                Yes
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            {children}
        </ConfirmContext.Provider>
    );
}

function useConfirm() {
    return useContext(ConfirmContext) as ConfirmContextType;
}

export { useConfirm, ConfirmProvider };
